import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X } from 'lucide-react';

const Modal = ({ title, isOpen, onClose, children, position = 'bottom' }) => {
  // Define animation variants based on position
  const getAnimationVariants = () => {
    if (position === 'center') {
      return {
        initial: { scale: 0.95, opacity: 0 },
        animate: { scale: 1, opacity: 1 },
        exit: { scale: 0.95, opacity: 0 }
      };
    }
    // Default: bottom
    return {
      initial: { y: '100%' },
      animate: { y: 0 },
      exit: { y: '100%' }
    };
  };

  // Define container classes based on position
  const getContainerClasses = () => {
    if (position === 'center') {
      return 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4';
    }
    // Default: bottom
    return 'fixed inset-0 bg-black bg-opacity-50 z-50 flex items-end justify-center';
  };

  // Define modal classes based on position
  const getModalClasses = () => {
    if (position === 'center') {
      return 'bg-white dark:bg-dark-bg rounded-lg shadow-xl w-full max-w-sm p-6 section';
    }
    // Default: bottom
    return 'bg-white dark:bg-dark-bg rounded-t-3xl w-full p-6 section';
  };

  const variants = getAnimationVariants();

  // Extract footer from children
  const footer = React.Children.toArray(children).find(
    child => child?.type === ModalFooter
  );
  const content = React.Children.toArray(children).filter(
    child => child?.type !== ModalFooter
  );

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className={getContainerClasses()}
          onClick={() => onClose?.(false)}
        >
          <motion.div
            initial={variants.initial}
            animate={variants.animate}
            exit={variants.exit}
            transition={{ type: 'tween', damping: 25, stiffness: 500, ease: 'easeInOut' }}
            className={`${getModalClasses()} max-h-[90vh] flex flex-col overflow-hidden`}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-semibold text-light-foreground dark:text-dark-foreground">{title}</h2>
              <button 
                onClick={() => onClose?.(false)} 
                className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-300"
              >
                <X size={24} />
              </button>
            </div>
            <div className="overflow-auto">
              {content}
            </div>
            {footer}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// Compound component for Modal Footer
const ModalFooter = ({ children, className = '' }) => {
  return (
    <div className={`sticky bottom-0 section border-none pt-2 mt-2 border-t border-gray-200 dark:border-gray-700 ${className}`}>
      {children}
    </div>
  );
};

Modal.Footer = ModalFooter;

export default Modal;
